import React from 'react';

import { Link } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { useApi } from '../../../api/use-api';
import { EmployeeDirectoryImportDownloadFileButton } from '../../../employee-directory-import/components/employee-directory-import-download-file-button';
import { EmployeeDirectoryImportMarkCompleteButton } from '../../../employee-directory-import/components/employee-directory-import-mark-complete-button';
import { EmployeeDirectoryImportUpdateButton } from '../../../employee-directory-import/components/employee-directory-update-file-button';
import { EmployeeDirectoryImportWizard } from '../../../employee-directory-import/components/wizard/employee-directory-import-wizard';
import { orgRoute } from '../../../web-routes';

import { formatDateTimeShort } from '../../../util/formatters';
import { SORT_ORDER, sortByDate } from '../../../util/sort-functions';
import Message from '../../../widgets/legacy-message';
import { LoadingMessage } from '../../../widgets/loading-message';
import * as AdminWebRoutes from '../../util/admin-web-routes';

type TableColumn = {
  key: string;
  label: string;
  component: (empImport: IncompleteEmployeeDirectoryImport) => JSX.Element;
};

const OrgLink = (empImport: IncompleteEmployeeDirectoryImport) => (
  <span className="u-whiteSpace-nowrap">
    <Link
      className="typo-link"
      to={`${orgRoute(empImport.orgId)}${AdminWebRoutes.ADMIN_BASE_ROUTE}${
        AdminWebRoutes.EMPLOYEE_DIRECTORY_IMPORTS
      }`}
    >
      {empImport.Organization.name}
    </Link>
  </span>
);

const SubmittedDate = (empImport: IncompleteEmployeeDirectoryImport) => (
  <div>{formatDateTimeShort(empImport.importedAt)}</div>
);

const FileActions = (empImport: IncompleteEmployeeDirectoryImport) => (
  <div>
    <EmployeeDirectoryImportDownloadFileButton employeeDirectoryImport={empImport} />
    <EmployeeDirectoryImportUpdateButton
      orgId={empImport.orgId}
      employeeDirectoryImportId={empImport.employeeDirectoryImportId}
    />
    <EmployeeDirectoryImportMarkCompleteButton
      employeeDirectoryImportId={empImport.employeeDirectoryImportId}
    />
  </div>
);

const ImportWizard = (empImport: IncompleteEmployeeDirectoryImport) => (
  <EmployeeDirectoryImportWizard employeeDirectoryImportId={empImport.employeeDirectoryImportId} />
);

const EmployeeImportRows = ({
  empImports,
  tableColumns,
}: {
  empImports: Array<IncompleteEmployeeDirectoryImport>;
  tableColumns: Array<TableColumn>;
}) => (
  <>
    {empImports.map((empImport) => (
      <TableRow key={`employee-directory-import-${empImport.employeeDirectoryImportId}`}>
        {tableColumns.map((col) => (
          <TableCell key={`col-${empImport.employeeDirectoryImportId}-${col.key}`}>
            {col.component(empImport)}
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export const IncompleteEmployeeDirectoryImports = () => {
  const { data: employeeImports, errorMessage } = useApi<Array<IncompleteEmployeeDirectoryImport>>({
    url: '/employee-directory-import/incomplete',
  });

  if (errorMessage) {
    return <Message variant="error">{errorMessage}</Message>;
  }

  if (!employeeImports) {
    return <LoadingMessage />;
  }

  const sortedEmployeeImports = employeeImports.sort(
    sortByDate((empImport) => empImport.importedAt, SORT_ORDER.ASC),
  );

  const tableColumns: Array<TableColumn> = [
    {
      key: 'org',
      label: 'Organization',
      component: OrgLink,
    },
    {
      key: 'submittedAt',
      label: 'Submitted At',
      component: SubmittedDate,
    },
    {
      key: 'fileActions',
      label: 'File Actions',
      component: FileActions,
    },
    {
      key: 'import',
      label: 'Import Employees',
      component: ImportWizard,
    },
  ];

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            {tableColumns.map((c) => (
              <TableCell key={`col-head-${c.key}`}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEmployeeImports.length ? (
            <EmployeeImportRows empImports={sortedEmployeeImports} tableColumns={tableColumns} />
          ) : (
            <TableRow>
              <TableCell colSpan={tableColumns.length}>
                <div className="typo-title u-textAlign-center">
                  No incomplete employee imports found
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};
